/**
 * v2 API contract of «План проектов» (/hours): what each project bills its client for a month
 * (docs/research/design2-month-ops.md §1, §6.1; decisions-log V11, V20, V22, V23).
 */
import { z } from 'zod';
import { isPeriod } from '../dates';
import { OFF_MAX_MINUTES } from './planDetail';
import type { ProjectPlanDetail } from './planDetail';

/** Hours to bill are capped at 9 999 h (design HR commit rule). */
export const HOURS_MAX_BILL_MINUTES = 9_999 * 60;

/** Tables of the screen, in order: «Почасовые проекты», «Фикс в месяц», «Фикс под ключ». */
export const HOURS_TABLE_KINDS = ['hourly', 'fixed', 'scope'] as const;
export type HoursTableKind = (typeof HOURS_TABLE_KINDS)[number];

/** Status of a project month (design `HST` keys used on the screen). */
export type HoursStatus = 'plan' | 'manual' | 'chat' | 'approvedProj' | 'invoiced' | 'changed' | 'reissued';

export const hoursPeriodSchema = z.string().refine(isPeriod, 'Месяц в формате ГГГГ-ММ');

export const hoursQuerySchema = z.object({
  month: hoursPeriodSchema,
  issuer: z.string().min(1).default('all'),
});
export type HoursQuery = z.infer<typeof hoursQuerySchema>;

/** The project's price in force for the month. */
export interface HoursPrice {
  readonly rateE4: number | null;
  readonly amountMinor: number | null;
  readonly currency: string;
}

export interface HoursRow {
  readonly projectId: string;
  readonly name: string;
  readonly clientName: string | null;
  /** Paused projects are dimmed. */
  readonly paused: boolean;
  readonly billing: HoursTableKind;
  readonly currency: string;
  /** A non-draft, non-cancelled invoice bills the month (V22): the value is read-only. */
  readonly locked: boolean;
  /** null → «нет суммы». */
  readonly price: HoursPrice | null;
  /** hourly: «План, ч» = Σ of the team's plans (V20) — the most the month can hold (V306). */
  readonly planMinutes: number | null;
  /** hourly: the fact while none is entered (V306): the team's facts, or the plan less the days off. */
  readonly expectedMinutes: number | null;
  /** hourly: entered hours to bill; null = not entered, `expectedMinutes` apply. */
  readonly minutes: number | null;
  /** hourly: «Выходные на проекте, ч» per person (V306); null = none. */
  readonly offMinutes: number | null;
  /** hourly: what the plan and the fact are made of (V307); null for the other tables. */
  readonly plan: ProjectPlanDetail | null;
  /** fixed / scope: entered amount; null = not entered. */
  readonly amountMinor: number | null;
  /** fixed: the prorated fee shown while nothing is entered. */
  readonly defaultAmountMinor: number | null;
  /** fixed: «неполный месяц». */
  readonly partialMonth: boolean;
  /** scope: the total and what earlier months billed. */
  readonly scope: { readonly totalMinor: number | null; readonly billedBeforeMinor: number } | null;
  readonly approved: boolean;
  readonly status: HoursStatus;
  /** The project month record (`project_period`) for the Activity focus; null before the first entry. */
  readonly recordId: string | null;
}

export interface HoursResponse {
  readonly period: string;
  readonly tables: Readonly<Record<HoursTableKind, readonly HoursRow[]>>;
}

/**
 * PUT /hours/projects/:projectId/:period — hours (hourly) or an amount (fixed, scope), or clear;
 * `offMinutes` — «Выходные на проекте, ч» of an hourly month (V306), null clears them.
 */
export const hoursUpdateSchema = z.union([
  z.object({ minutes: z.number().int().min(0).max(HOURS_MAX_BILL_MINUTES) }).strict(),
  z.object({ amountMinor: z.number().int().min(0) }).strict(),
  z.object({ offMinutes: z.number().int().min(0).max(OFF_MAX_MINUTES).nullable() }).strict(),
  z.object({ clear: z.literal(true) }).strict(),
]);
export type HoursUpdate = z.infer<typeof hoursUpdateSchema>;

/**
 * POST /hours/paste — a pasted column, applied atomically. `value` is in stored units: minutes for
 * an hourly project, minor units for a fixed or scope project.
 */
export const hoursPasteSchema = z.object({
  cells: z
    .array(
      z.object({
        projectId: z.string().min(1),
        period: hoursPeriodSchema,
        value: z.number().int().min(0),
      }),
    )
    .min(1)
    .max(500),
});
export type HoursPaste = z.infer<typeof hoursPasteSchema>;

/** Result of every write: the design's Activity text and the server undo token. */
export interface HoursWriteResult {
  readonly message: string;
  readonly undoToken: string | null;
}
